import { useCallback, useEffect, useState } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app, auth } from './firebase'
import type { Plan } from './plans'

/* ============================================================================
   Admin client.

   Every call lands on a Cloud Function that re-checks the claim server-side.
   The client-side check below decides what to *render*; it is not the security
   boundary and must never be treated as one.
   ============================================================================ */

const functions = getFunctions(app)

function call<Request, Response>(name: string) {
  const callable = httpsCallable<Request, Response>(functions, name)
  return async (data: Request): Promise<Response> => {
    try {
      const response = await callable(data)
      return response.data
    } catch (caught) {
      const error = caught as { code?: string; message?: string }
      if (error.code === 'functions/permission-denied') {
        throw new Error("You don't have access to that.")
      }
      if (error.code === 'functions/not-found') {
        throw new Error(
          'The admin functions are not deployed yet. Run: firebase deploy --only functions:adminWhoAmI,functions:adminListUsers,functions:adminGetUser,functions:adminSetUserFlags,functions:adminStats,functions:adminDataDoctor,functions:adminApplyFix',
        )
      }
      throw new Error(error.message ?? 'That call failed.')
    }
  }
}

/* -------------------------------------------------------------------- types */

export interface AdminUserRow {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  disabled: boolean
  isAdmin: boolean
  isSuperAdmin: boolean
  createdAt: string | null
  lastSignInAt: string | null
  providers: string[]
  emailVerified: boolean
  role: string | null
  onboarded: boolean
  tourCount: number
}

export interface AdminUserDetail {
  account: {
    uid: string
    email: string | null
    displayName: string | null
    photoURL: string | null
    disabled: boolean
    emailVerified: boolean
    isAdmin: boolean
    isSuperAdmin: boolean
    createdAt: string | null
    lastSignInAt: string | null
    providers: string[]
  }
  profile: Record<string, unknown> | null
  fcmTokenCount: number
  ownedTours: Array<{
    id: string
    tourName: string
    artist: string
    startDate: number | null
    endDate: number | null
  }>
  crewOn: Array<{
    id: string
    tourId: string
    tourName: string
    status: string
    accessLevel: string | null
    roles: string[]
  }>
  unreadNotifications: number
}

/** One rolling seven-day window. Never a calendar week — a week in progress
 *  renders as a collapse in the last column, which is a lie. */
export interface StatWeek {
  start: number
  end: number
  signups: number
  tours: number
  shows: number
}

export type ActivityKind = 'signup' | 'tour' | 'show' | 'crew'

export interface ActivityEvent {
  id: string
  kind: ActivityKind
  at: number
  title: string
  detail: string
}

export interface AdminStats {
  users: {
    total: number
    signupsLast30: number
    signupsPrev30: number
    unverified: number
    disabled: number
    truncated: boolean
  }
  tours: {
    total: number
    active: number
    upcoming: number
    createdLast30: number
    createdPrev30: number
    shows: number
    showsNext30: number
    showsPerTour: number
    crewPerTour: number
  }
  activation: {
    withAtLeastOneTour: number
    activationRate: number
    repeatPlanners: number
    repeatRate: number
  }
  invites: { invited: number; accepted: number; acceptanceRate: number }
  weeks: StatWeek[]
  activity: ActivityEvent[]
  generatedAt: number
}

export interface DoctorFinding {
  id: string
  title: string
  detail: string
  severity: 'high' | 'medium' | 'low'
  count: number
  fixable: boolean
  samples: string[]
}

/* ------------------------------------------------------------------- calls */

export const adminListUsers = call<{ pageToken?: string }, {
  users: AdminUserRow[]
  nextPageToken: string | null
}>('adminListUsers')

export const adminGetUser = call<{ uid: string }, AdminUserDetail>('adminGetUser')

export const adminSetUserFlags = call<
  { uid: string; disabled?: boolean; isAdmin?: boolean; isSuperAdmin?: boolean },
  { ok: boolean }
>('adminSetUserFlags')

export const adminStats = call<Record<string, never>, AdminStats>('adminStats')

export const adminDataDoctor = call<
  Record<string, never>,
  { findings: DoctorFinding[]; scannedAt: number }
>('adminDataDoctor')

export const adminApplyFix = call<{ findingId: string }, { changed: number }>('adminApplyFix')

/* ------------------------------------------------------------------- plans */

/** A plan as the editor sees it: values plus where they came from. */
export type AdminPlanRow = Plan & {
  /** True when a `plans/{id}` document exists. False means it is running on
   *  the built-in default and there is nothing to edit yet. */
  isStored: boolean
  hasDefault: boolean
}

export const adminListPlans = call<Record<string, never>, { plans: AdminPlanRow[] }>(
  'adminListPlans',
)

export const adminSavePlan = call<
  { planId: string; plan: Partial<Plan> },
  { ok: boolean }
>('adminSavePlan')

export const adminSeedPlans = call<
  Record<string, never>,
  { written: number; skipped: number }
>('adminSeedPlans')

/**
 * Overrides an account's plan. `planId: null` clears the override.
 *
 * Writes `users/{uid}.planOverride`, never the subscription record — billing
 * owns that and would overwrite a comp on the next webhook. Resolution takes
 * whichever of the two grants more, so this works on a paying account and can
 * never quietly downgrade one.
 *
 * `days` is a duration, not a date: "give them Pro for three months" is the
 * actual request, and letting the server turn it into a timestamp means the
 * caller can't get the timezone wrong. Omit it for no expiry.
 */
export const adminSetPlanOverride = call<
  { uid: string; planId: string | null; days?: number | null; note?: string },
  { ok: boolean; planId: string | null; expiresAt?: number | null }
>('adminSetPlanOverride')

/**
 * Claims the first admin. Refuses once anyone holds the claim, so it can be
 * used exactly once and is inert afterwards.
 */
export const adminBootstrap = call<Record<string, never>, { ok: boolean; email: string }>(
  'adminBootstrap',
)

/* ------------------------------------------------------------------- claim */

/**
 * Whether the signed-in user holds the admin claim.
 *
 * Forces a token refresh once on mount — a claim granted or revoked while
 * someone is signed in doesn't reach their cached token otherwise, so a
 * revoked admin would keep seeing the portal until it expired.
 */
export function useIsAdmin(): {
  isAdmin: boolean
  isSuperAdmin: boolean
  checking: boolean
  /** Re-reads the token. Call after anything that changes a claim. */
  recheck: () => Promise<void>
} {
  const [isAdmin, setIsAdmin] = useState(false)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [checking, setChecking] = useState(true)

  const check = useCallback(async () => {
    const user = auth.currentUser
    if (!user) {
      setIsAdmin(false)
      setIsSuperAdmin(false)
      setChecking(false)
      return
    }
    try {
      // Forced refresh: a claim granted a moment ago is not in the cached
      // token, and the whole point of this hook is to notice that it changed.
      const token = await user.getIdTokenResult(true)
      setIsAdmin(token.claims.admin === true)
      setIsSuperAdmin(token.claims.superAdmin === true)
    } catch {
      setIsAdmin(false)
      setIsSuperAdmin(false)
    } finally {
      setChecking(false)
    }
  }, [])

  useEffect(() => {
    void check()
  }, [check])

  return { isAdmin, isSuperAdmin, checking, recheck: check }
}

/* --------------------------------------------------------------- prospects */

export interface AdminProspectRow {
  orgId: string
  ownerUid: string
  name: string
  intendedEmail: string | null
  note: string | null
  claimedAt: { seconds: number } | null
  claimedBy: string | null
  tourCount: number
  artistCount: number
}

export const adminCreateProspect = call<
  { name: string; intendedEmail?: string; note?: string },
  { orgId: string; ownerUid: string }
>('adminCreateProspect')

export const adminListProspects = call<
  Record<string, never>,
  { prospects: AdminProspectRow[] }
>('adminListProspects')

export const adminActAs = call<
  { orgId?: string; uid?: string; minutes?: number },
  { orgId: string; ownerUid: string; orgName: string; until: number }
>('adminActAs')

export const adminStopActingAs = call<Record<string, never>, { released: boolean }>(
  'adminStopActingAs',
)

export const adminSendClaimLink = call<
  { orgId: string; email: string },
  { sent: boolean; expiresAt: string }
>('adminSendClaimLink')

export const claimProspectAccount = call<
  { token: string },
  { orgId: string; toursTransferred: number }
>('claimProspectAccount')
